/**
 * OVERRIDE CheckboxWidget.jsx
 * REASON: Language independent icon along side with title field.
 * FILE: https://raw.githubusercontent.com/plone/volto/refs/heads/16.x.x/src/components/manage/Widgets/CheckboxWidget.jsx
 * FILE VERSION: Volto 16.x
 * TICKET: https://gitlab.dlr.de/internet-cms/cms-plone/dlr-internet/-/issues/3448
 * DATE: 2026-01-13
 * DEVELOPER: @davisagli
 */

/**
 * CheckboxWidget component.
 * @module components/manage/Widgets/CheckboxWidget
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from 'semantic-ui-react';
import { Icon } from '@plone/volto/components';

import { defineMessages, injectIntl } from 'react-intl';
import { FormFieldWrapper } from '@plone/volto/components';
import LanguageSVG from '@plone/volto/icons/language.svg';

const messages = defineMessages({
  language_independent_icon_title: {
    id: 'Language independent icon title',
    defaultMessage:
      'This field is a language-independent field. Any value you enter here will be synced between the translations when you save this form.',
  },
});

/**
 * CheckboxWidget component class.
 * @function CheckboxWidget
 * @returns {string} Markup of the component.
 *
 * To use it, in schema properties, declare a field like:
 *
 * ```jsx
 * {
 *  title: "Active",
 *  type: 'boolean',
 * }
 * ```
 */
const CheckboxWidget = (props) => {
  const {
    id,
    intl,
    title,
    value,
    onChange,
    isDisabled,
    multilingual_options,
  } = props;

  return (
    <FormFieldWrapper {...props} columns={1}>
      <div className="wrapper">
        <Checkbox
          name={`field-${id}`}
          checked={value || false}
          disabled={isDisabled}
          onChange={(event, { checked }) => {
            onChange(id, checked);
          }}
          label={
            <label htmlFor={`field-${id}`}>
              {title} {/* BEGIN CUSTOMIZATION */}
              {multilingual_options?.language_independent && (
                <div className="languageIndependent-icon">
                  <Icon
                    title={intl.formatMessage(
                      messages.language_independent_icon_title,
                    )}
                    name={LanguageSVG}
                    size="24px"
                    color="#555"
                  />
                </div>
              )}
              {/* END CUSTOMIZATION */}
            </label>
          }
        />
      </div>
    </FormFieldWrapper>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
CheckboxWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.bool,
  onChange: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  wrapped: PropTypes.bool,
};

/**
 * Default properties.
 * @property {Object} defaultProps Default properties.
 * @static
 */
CheckboxWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
  onChange: null,
  onEdit: null,
  onDelete: null,
};

export default injectIntl(CheckboxWidget);
